import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { barterCoinApi, userApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Alert from '@/components/ui/Alert'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import { formatCurrency, formatDate } from '@/utils/format'
import { Coins, CreditCard, Send, DollarSign, ArrowUpRight, ArrowDownLeft, Lock, Info, Search, X } from 'lucide-react'

const COIN_PACKAGES = [
  { amount: 25, label: '25 Coins', price: 26.25 },
  { amount: 50, label: '50 Coins', price: 52.50 },
  { amount: 100, label: '100 Coins', price: 105.00 },
  { amount: 500, label: '500 Coins', price: 525.00 },
  { amount: 1000, label: '1000 Coins', price: 1050.00 },
]

export default function BarterCoins() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null)
  const [transferAmount, setTransferAmount] = useState('')
  const [transferEmail, setTransferEmail] = useState('')
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Handle purchase success/cancel from Stripe redirect
  useEffect(() => {
    const purchaseStatus = searchParams.get('purchase')
    if (purchaseStatus === 'success') {
      setSuccess('Payment successful! Your coins have been added to your balance.')
      // Refresh balance and transactions
      queryClient.invalidateQueries({ queryKey: ['coin-balance'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      // Clean up URL - use replace to avoid triggering effect again
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('purchase')
      setSearchParams(newSearchParams, { replace: true })
    } else if (purchaseStatus === 'cancelled') {
      setError('Payment was cancelled. Please try again if you wish to purchase coins.')
      // Clean up URL - use replace to avoid triggering effect again
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('purchase')
      setSearchParams(newSearchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount to check for redirect params

  const { data: transactionsData, isLoading } = useQuery({
    queryKey: ['transactions'],
    queryFn: () => barterCoinApi.getTransactions(),
    staleTime: 30000, // 30 seconds
    refetchOnMount: false,
  })

  const { data: balanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ['coin-balance'],
    queryFn: () => barterCoinApi.getBalance(),
    staleTime: 10000, // 10 seconds
    refetchOnMount: false,
  })

  const purchaseMutation = useMutation({
    mutationFn: (amount: number) => barterCoinApi.purchase(amount),
    onSuccess: (response: any) => {
      const checkoutUrl = response?.data?.checkout_url
      if (checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl
      } else {
        setError('Checkout URL not provided')
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Purchase failed')
    },
  })

  const transferMutation = useMutation({
    mutationFn: ({ email, amount }: { email: string; amount: number }) =>
      barterCoinApi.transfer(email, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['coin-balance'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      setTransferEmail('')
      setTransferAmount('')
      setSuccess('Transfer successful!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Transfer failed')
    },
  })

  const withdrawMutation = useMutation({
    mutationFn: (amount: number) => barterCoinApi.withdraw(amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['coin-balance'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      setWithdrawAmount('')
      setSuccess('Withdrawal request submitted! Funds will be processed within 2-3 business days.')
      setTimeout(() => setSuccess(''), 5000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Withdrawal failed')
    },
  })

  const transactions = transactionsData?.data?.transactions || []
  const balanceInfo = (balanceData?.data || {
    total_balance: user?.barter_coin_balance || 0,
    available_balance: user?.barter_coin_balance || 0,
    held_in_escrow: 0,
    escrow_details: []
  }) as {
    total_balance: number
    available_balance: number
    held_in_escrow: number
    escrow_details: any[]
  }

  const handlePurchase = () => {
    if (!selectedPackage) return
    setError('')
    purchaseMutation.mutate(selectedPackage)
  }

  const handleTransfer = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const amount = parseInt(transferAmount)
    if (isNaN(amount) || amount <= 0 || !Number.isInteger(parseFloat(transferAmount))) {
      setError('Please enter a whole number for coin amount')
      return
    }
    if (!transferEmail) {
      setError('Recipient email is required')
      return
    }
    transferMutation.mutate({ email: transferEmail, amount })
  }

  const handleWithdraw = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const amount = parseFloat(withdrawAmount)
    if (isNaN(amount) || amount <= 0) {
      setError('Invalid withdrawal amount')
      return
    }
    if (amount > (user?.barter_coin_balance || 0)) {
      setError('Insufficient balance')
      return
    }
    withdrawMutation.mutate(amount)
  }

  const performUserSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([])
      setShowSearchResults(false)
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    try {
      const response = await userApi.search(query)
      if (response.success && response.data?.users) {
        // Filter out current user from results
        const filtered = response.data.users.filter((u: any) => u.id !== user?.id)
        setSearchResults(filtered)
        setShowSearchResults(true)
      }
    } catch (err) {
      console.error('Failed to search users:', err)
      setSearchResults([])
      setShowSearchResults(false)
    } finally {
      setIsSearching(false)
    }
  }, [user?.id])

  const handleUserSearch = useCallback((query: string) => {
    setUserSearchQuery(query)

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (query.length < 2) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }

    // Debounce the search
    searchTimeoutRef.current = setTimeout(() => {
      performUserSearch(query)
    }, 300) // 300ms debounce
  }, [performUserSearch])

  const selectUser = (selectedUser: any) => {
    setTransferEmail(selectedUser.email)
    setUserSearchQuery(`${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`)
    setShowSearchResults(false)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  if (isLoading || balanceLoading) return <Loading />

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold">Barter Coins</h1>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Balance */}
        <Card className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100 mb-2">Total Balance</p>
              <p className="text-4xl font-bold">{balanceInfo.total_balance}</p>
              <p className="text-primary-100 mt-1 text-sm">Barter Coins</p>
            </div>
            <Coins className="w-16 h-16 text-primary-300" />
          </div>
        </Card>

        {/* Available Balance */}
        <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 mb-2">Available Balance</p>
              <p className="text-4xl font-bold">{balanceInfo.available_balance}</p>
              <p className="text-green-100 mt-1 text-sm">Ready to Use</p>
            </div>
            <Coins className="w-16 h-16 text-green-300" />
          </div>
        </Card>

        {/* Held in Escrow */}
        <Card className="bg-gradient-to-r from-orange-600 to-orange-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 mb-2">Held in Escrow</p>
              <p className="text-4xl font-bold">{balanceInfo.held_in_escrow}</p>
              <p className="text-orange-100 mt-1 text-sm">For Active Barters</p>
            </div>
            <Lock className="w-16 h-16 text-orange-300" />
          </div>
        </Card>
      </div>

      {/* Escrow Information Alert */}
      {balanceInfo.held_in_escrow > 0 && (
        <Alert
          type="info"
          message={
            <div className="flex items-start space-x-2">
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Coins Held in Escrow</p>
                <p className="text-sm mt-1">
                  You have <strong>{balanceInfo.held_in_escrow} coins</strong> temporarily held in escrow for your active barters.
                  These coins will be automatically refunded to your available balance when your barter groups are completed and there are no conflicts.
                </p>
              </div>
            </div>
          }
        />
      )}

      {/* Escrow Details */}
      {balanceInfo.escrow_details && balanceInfo.escrow_details.length > 0 && (
        <Card title="Escrow Breakdown">
          <div className="space-y-3">
            {balanceInfo.escrow_details.map((detail: any) => (
              <div key={detail.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{detail.title}</p>
                  <div className="flex items-center space-x-4 mt-1">
                    <Badge status={detail.status}>{detail.status}</Badge>
                    {detail.group_id && (
                      <span className="text-sm text-gray-500">Group #{detail.group_id}</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-orange-600">{detail.escrow_amount}</p>
                  <p className="text-xs text-gray-500">coins held</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Purchase Coins */}
        <Card title="Purchase Coins" className="lg:col-span-2">
          <p className="text-sm text-gray-600 mb-4">
            1 Barter Coin = $1.00 + 5% processing fee
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {COIN_PACKAGES.map((pkg) => (
              <button
                key={pkg.amount}
                onClick={() => setSelectedPackage(pkg.amount)}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedPackage === pkg.amount
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                <p className="text-2xl font-bold text-gray-900">{pkg.amount}</p>
                <p className="text-sm text-gray-500">coins</p>
                <p className="text-lg font-semibold text-primary-600 mt-2">
                  ${pkg.price.toFixed(2)}
                </p>
              </button>
            ))}
          </div>
          <Button
            className="w-full mt-6"
            disabled={!selectedPackage}
            onClick={handlePurchase}
            isLoading={purchaseMutation.isPending}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Purchase {selectedPackage || 0} Coins
          </Button>
        </Card>

        {/* Coin Info */}
        <Card title="Coin Information">
          <div className="space-y-4 text-sm">
            <div className="flex items-start space-x-2">
              <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-semibold">Purchase Rate</p>
                <p className="text-gray-600">$1.00 + 5% fee</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <ArrowDownLeft className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-semibold">Withdrawal Rate</p>
                <p className="text-gray-600">$0.95 per coin</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <ArrowUpRight className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <div>
                <p className="font-semibold">Transfer</p>
                <p className="text-gray-600">Free between users</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Transfer Coins */}
        <Card title="Transfer Coins">
          <form onSubmit={handleTransfer} className="space-y-4">
            <div className="relative">
              <label className="label">Search Recipient *</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  className="input pl-10 pr-10"
                  placeholder="Search by name or email..."
                  value={userSearchQuery}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  autoComplete="off"
                />
                {isSearching ? (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                  </div>
                ) : userSearchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setUserSearchQuery('')
                      setTransferEmail('')
                      setSearchResults([])
                      setShowSearchResults(false)
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map((u: any) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => selectUser(u)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                          {u.first_name.charAt(0)}{u.last_name.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {u.first_name} {u.last_name}
                          </p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                          {u.profession && (
                            <p className="text-xs text-gray-400">{u.profession}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {showSearchResults && searchResults.length === 0 && userSearchQuery.length >= 2 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
                  No users found
                </div>
              )}

              {transferEmail && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Selected: {transferEmail}
                </p>
              )}
            </div>

            <div>
              <label className="label">Amount (Whole Coins) *</label>
              <input
                type="number"
                step="1"
                min="1"
                className="input"
                placeholder="0"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter whole numbers only (e.g., 5, 10, 25)
              </p>
            </div>
            <Button
              type="submit"
              className="w-full"
              isLoading={transferMutation.isPending}
              disabled={!transferEmail}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Coins
            </Button>
          </form>
        </Card>

        {/* Withdraw Coins */}
        <Card title="Withdraw Coins">
          {!user?.withdrawal_type ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 mb-3">
                <strong>Withdrawal Settings Required</strong>
              </p>
              <p className="text-sm text-yellow-700 mb-3">
                Before you can withdraw funds, you need to configure your withdrawal settings in your profile.
              </p>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => navigate('/dashboard/profile')}
              >
                Configure Withdrawal Settings
              </Button>
            </div>
          ) : (
            <form onSubmit={handleWithdraw} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Withdrawal Method:</strong> {user.withdrawal_type === 'zelle' ? 'Zelle' : user.withdrawal_type === 'paypal' ? 'PayPal' : 'ACH Bank Transfer'}
                </p>
                {user.withdrawal_type === 'zelle' && user.zelle_email && (
                  <p className="text-xs text-blue-600 mt-1">{user.zelle_email}</p>
                )}
                {user.withdrawal_type === 'paypal' && user.paypal_email && (
                  <p className="text-xs text-blue-600 mt-1">{user.paypal_email}</p>
                )}
                {user.withdrawal_type === 'ach' && user.ach_bank_name && (
                  <p className="text-xs text-blue-600 mt-1">{user.ach_bank_name} - {user.ach_account_type}</p>
                )}
              </div>

              <div>
                <label className="label">Amount to Withdraw *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={user?.barter_coin_balance || 0}
                  className="input"
                  placeholder="0.00"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  You will receive ${(parseFloat(withdrawAmount) * 0.95).toFixed(2)} at $0.95 per coin
                </p>
              </div>
              <Button
                type="submit"
                variant="secondary"
                className="w-full"
                isLoading={withdrawMutation.isPending}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Request Withdrawal
              </Button>
            </form>
          )}
        </Card>
      </div>

      {/* Transaction History */}
      <Card title="Transaction History">
        <ResponsiveTable>
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coins</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount (USD)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {transactions.map((transaction: any) => (
                <tr key={transaction.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(transaction.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={
                      transaction.transaction_type.includes('in') || transaction.transaction_type === 'purchase'
                        ? 'success'
                        : 'warning'
                    }>
                      {transaction.transaction_type.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {transaction.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-semibold">
                    <span className={
                      transaction.transaction_type.includes('in') || transaction.transaction_type === 'purchase'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }>
                      {transaction.transaction_type.includes('in') || transaction.transaction_type === 'purchase' ? '+' : '-'}
                      {parseFloat(transaction.coin_amount).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-semibold">
                    <span className={
                      transaction.transaction_type.includes('in') || transaction.transaction_type === 'purchase'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }>
                      {transaction.amount ? formatCurrency(transaction.amount) : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge status={transaction.status}>
                      {transaction.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResponsiveTable>

        {transactions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No transactions yet</p>
          </div>
        )}
      </Card>
    </div>
  )
}
